import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { EditIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useFormat } from '../../../../hooks/index';
import { safeCapTypeValuationCapTitle } from '../../../../types/safes.types';
import { openSelectedCompanyStorageUrlInNewTab } from '../../../../utils/company-storage-url.util';
import { RecordAttachedFilesPreview } from '../../../pool-plans/components/record-attached-files-preview';
import { ChildrenFormProps, EditSafeStepVariant } from '../modals.types';

export type EditPreviewProps = {
  onEditClick: (step: EditSafeStepVariant) => void;
  previewMode?: true;
} & ChildrenFormProps;

export const EditPreview: FC<EditPreviewProps> = ({
  handleCloseModal,
  formData,
  onEditClick,
  previewMode,
  docusignFileLinks = [],
}) => {
  const { stepOne, stepTwo, stepThree } = formData();
  const { format } = useFormat();
  const handleDocumentClick = (link: string) => {
    void openSelectedCompanyStorageUrlInNewTab(link);
  };

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <div className="flex w-full justify-between px-4">
            <span className="text-sm font-[550] text-gray-700">
              <AppFormattedMessage id="GENERAL" />
            </span>
            {!previewMode && (
              <Button className="w-fit" onClick={() => onEditClick(1)} styleType="NONE">
                <EditIcon iconColor="#2565C8" />
              </Button>
            )}
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={<AppFormattedMessage id="SHAREHOLDER" />}
              value={
                <Tooltip content={stepOne?.stakeholder?.fullName}>
                  {stepOne?.stakeholder?.fullName}
                </Tooltip>
              }
            />

            <SummaryItem
              title={<AppFormattedMessage id="INVESTMENT" />}
              value={stepOne?.investment && Number(stepOne?.investment).toLocaleString('en-US')}
            />
            <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="ISSUE_DATE" />}
              value={format(stepOne?.issueDate, 'dd/MM/yyyy')}
            />
          </div>
        </div>
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <div className="flex w-full justify-between px-4">
            <span className="text-sm font-[550] text-gray-700">
              <AppFormattedMessage id="CAP_OR_DISCOUNT_DETAILS" />
            </span>
            {!previewMode && (
              <Button className="w-fit" onClick={() => onEditClick(2)} styleType="NONE">
                <EditIcon iconColor="#2565C8" />
              </Button>
            )}
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={
                <AppFormattedMessage
                  id={
                    stepTwo?.cap?.capValue && stepTwo.cap.capType
                      ? safeCapTypeValuationCapTitle[stepTwo.cap.capType]
                      : 'CAP'
                  }
                />
              }
              value={
                stepTwo?.cap?.capValue
                  ? Number(stepTwo?.cap?.capValue).toLocaleString('en-US')
                  : '-'
              }
            />

            <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="DISCOUNT" />}
              value={
                stepTwo?.discount?.discountValue ? `${stepTwo?.discount?.discountValue}%` : '-'
              }
            />
          </div>
        </div>
        {stepTwo?.floor?.enabled && (
          <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
            <div className="flex w-full justify-between px-4">
              <span className="text-sm font-[550] text-gray-700">
                <AppFormattedMessage id="FLOOR_AND_MATURITY_DATE" />
              </span>
              {!previewMode && (
                <Button className="w-fit" onClick={() => onEditClick(2)} styleType="NONE">
                  <EditIcon iconColor="#2565C8" />
                </Button>
              )}
            </div>
            <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
              <SummaryItem
                className="pt-0"
                title={<AppFormattedMessage id="FLOOR" />}
                value={
                  stepTwo?.floor?.floorValue
                    ? Number(stepTwo?.floor?.floorValue).toLocaleString('en-US')
                    : '-'
                }
              />
              <SummaryItem
                className="pb-0"
                title={<AppFormattedMessage id="MATURITY_DATE" />}
                value={
                  stepTwo?.floor?.maturityDate
                    ? format(stepTwo?.floor?.maturityDate, 'dd/MM/yyyy')
                    : '-'
                }
              />
            </div>
          </div>
        )}

        <div className="flex w-full items-center justify-between rounded-lg border border-gray-100 bg-white px-4 py-2">
          {(docusignFileLinks?.length ?? 0) > 0 ? (
            <RecordAttachedFilesPreview
              docusignFileLinks={docusignFileLinks}
              files={stepThree?.files}
              onDocumentClick={previewMode ? handleDocumentClick : undefined}
              previewMode={previewMode}
            />
          ) : (
            <div className="flex w-full gap-3 py-2">
              <span className="w-[120px] shrink-0 text-xs font-[450] text-gray-500">
                <AppFormattedMessage id="ATTACHED_FILES_TITLE" />:
              </span>
              <RecordAttachedFilesPreview
                files={stepThree?.files}
                onDocumentClick={previewMode ? handleDocumentClick : undefined}
                previewMode={previewMode}
              />
            </div>
          )}
          {!previewMode && (
            <Button className="w-fit" onClick={() => onEditClick(3)} styleType="NONE">
              <EditIcon iconColor="#2565C8" />
            </Button>
          )}
        </div>
        <div className="flex w-full items-start justify-between rounded-lg border border-gray-100 bg-white px-4 py-2">
          <div className="flex w-full gap-3">
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
            </span>
            <div
              className={twMerge(
                'max-w-[250px] break-words text-xs font-[450] text-gray-700',
                !previewMode ? 'max-w-[220px]' : 'max-w-[250px]',
              )}
            >
              {stepThree?.additionalNotes || '-'}
            </div>
          </div>
          {!previewMode && (
            <Button className="w-fit" onClick={() => onEditClick(3)} styleType="NONE">
              <EditIcon iconColor="#2565C8" />
            </Button>
          )}
        </div>
      </div>

      <div
        className={twMerge(
          'flex h-9 w-full justify-start gap-3 px-6',
          previewMode && 'justify-end',
        )}
      >
        <Button
          className={twMerge(
            'w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700',
            previewMode && 'bg-brand-700 text-white',
          )}
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          {previewMode ? <AppFormattedMessage id="CLOSE" /> : <AppFormattedMessage id="CANCEL" />}
        </Button>
      </div>
    </>
  );
};
